// Turns an axios error from the REST API into a short, readable German message.
// The full error is logged to the console for debugging.

const DEFAULT_FIELD_LABELS = {
  non_field_errors: '',
  detail: '',
  valid_from: 'Gültig ab',
  valid_notafter: 'Gültig bis',
  inherit: 'Vererbung',
  tags: 'Sektionen',
  type: 'Typ',
  organization: 'Organisation',
  min_amount_donation: 'Mindestbetrag Spende',
  min_amount_collection: 'Mindestbetrag Kollekte',
  tax_exempt_purpose: 'Gemeinnütziger Zweck',
  template_file: 'Vorlagen',
  slug: 'Slug',
  payment_device: 'Zahlungsgerät'
}

// The backend's own validation messages are English; translate the ones shown in this UI
const MESSAGE_TRANSLATIONS = {
  'This feature type can only be created in the admin': 'Dieses Feature kann nur im Django-Admin angelegt werden.',
  'Only collection plan section features have tags': 'Nur Kollektenplan-Sektionen haben Sektionen.',
  'valid_notafter must not be before valid_from': '„Gültig bis“ darf nicht vor „Gültig ab“ liegen.',
  'The organization already has this feature in the given period': 'Die Organisation hat dieses Feature im gewählten Zeitraum bereits.',
  'This feature type requires a payment device': 'Bitte ein Zahlungsgerät auswählen.',
  'The payment device has no valid online payment contract for this organization': 'Das Zahlungsgerät hat keinen gültigen Online-Zahlungsvertrag für diese Organisation.',
  'This feature type requires a slug': 'Bitte einen Slug angeben.',
  'This slug is already taken': 'Dieser Slug ist bereits vergeben.',
  'The slug may only contain letters, numbers, dots and dashes.': 'Der Slug darf nur Buchstaben, Zahlen, Punkte und Bindestriche enthalten.'
}

const MAX_LENGTH = 300

// DRF errors are strings, lists or (nested) objects of those
const collectMessages = (data, labels, path = []) => {
  if (data === null || data === undefined) return []
  if (typeof data === 'string') {
    const message = MESSAGE_TRANSLATIONS[data] ?? data
    const label = path.map(key => labels[key] ?? key).filter(part => part && !/^\d+$/.test(part)).join(' / ')
    // Translated messages already name the field
    return [label && !MESSAGE_TRANSLATIONS[data] ? `${label}: ${message}` : message]
  }
  if (Array.isArray(data)) {
    return data.flatMap(item => collectMessages(item, labels, path))
  }
  if (typeof data === 'object') {
    return Object.entries(data).flatMap(([key, value]) => collectMessages(value, labels, [...path, key]))
  }
  return [String(data)]
}

const truncate = text => text.length > MAX_LENGTH ? `${text.slice(0, MAX_LENGTH)}…` : text

export const formatApiError = (error, { fallback = 'Fehler beim Speichern.', fieldLabels = {} } = {}) => {
  // eslint-disable-next-line no-console
  console.error(error)

  const response = error?.response
  if (!response) {
    return 'Der Server ist nicht erreichbar. Bitte die Verbindung prüfen und erneut versuchen.'
  }

  const { status, data } = response
  // HTML error pages (e.g. an endpoint missing on this server) are useless as message
  const isHtml = typeof data === 'string' && /^\s*</.test(data)

  // Long plain-text bodies are usually debug output, not a message; a 405 means the
  // server does not offer this action yet, which says more than DRF's generic text
  const isUsable = status !== 405 && !!data && !isHtml && (typeof data !== 'string' || data.length <= MAX_LENGTH)
  if (isUsable) {
    const messages = collectMessages(data, { ...DEFAULT_FIELD_LABELS, ...fieldLabels })
    if (messages.length) {
      return truncate(messages.join(' '))
    }
  }

  if (status === 401 || status === 403) {
    return `Keine Berechtigung für diese Aktion (HTTP ${status}).`
  }
  if (status === 404 || status === 405) {
    return `Diese Funktion ist auf diesem Server (noch) nicht verfügbar (HTTP ${status}).`
  }
  if (status >= 500) {
    return `Interner Serverfehler (HTTP ${status}). Bitte später erneut versuchen.`
  }
  return `${fallback} (HTTP ${status})`
}
